import { useState, ReactNode, useMemo } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DateTimePicker,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";

export interface ScheduleMessageDialogProps {
  children?: ReactNode;
  onSchedule: (sendAt: Date) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initDate?: Date;
}

export const ScheduleMessageDialog = ({
  children,
  onSchedule,
  open,
  onOpenChange,
  initDate,
}: ScheduleMessageDialogProps) => {
  const { t } = useTranslation("inbox");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initDate);
  const [isOpen, setIsOpen] = useState(false);

  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : isOpen;
  const setDialogOpen = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setIsOpen(newOpen);
    }
  };

  // Check if the selected date is in the future
  const isFutureDate = useMemo(() => {
    if (!selectedDate) return false;
    return selectedDate.getTime() > Date.now();
  }, [selectedDate]);

  const handleSchedule = () => {
    if (selectedDate && isFutureDate) {
      onSchedule(selectedDate);
      setSelectedDate(undefined);
      setDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setSelectedDate(undefined);
    setDialogOpen(false);
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!!children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Schedule for")}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <DateTimePicker
            value={selectedDate}
            onChange={setSelectedDate}
            label=""
            placeholder="Select date and time"
            disabledDates={{ before: new Date() }}
          />
          {selectedDate && !isFutureDate && (
            <p className="text-sm text-destructive mt-2">{t("Please select a future date and time")}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={!selectedDate || !isFutureDate}
            data-trackingid="schedule-message-confirm"
          >
            {t("Schedule and Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
